import { useEffect, useId } from "react";
import { CHANGELOG } from "../../changelogContent";

export interface ChangelogModalProps {
    open: boolean;
    onClose: () => void;
    /** For `aria-controls` on the opener control */
    dialogId: string;
}

function renderChangelogItem(item: string) {
    const trimmed = item.trimStart();
    const leadingWhitespaceLength = item.length - trimmed.length;
    const leadingWhitespace = item.slice(0, leadingWhitespaceLength);
    if (!/^new\b/i.test(trimmed)) return item;

    const withoutNew = trimmed.replace(/^new\s+/i, "");
    const splitPatterns = [
        /\s+lets\s+/i,
        /\s+for\s+/i,
        /\s+with\s+/i,
        /\s+now\s+/i,
        /\s+can\s+/i,
        /\s+plus\s+/i,
        /:\s+/,
        /\.\s+/,
        /\s+-\s+/,
        /\s+-\s+/,
    ];

    let splitIndex = -1;
    let splitLength = 0;
    for (const pattern of splitPatterns) {
        const match = pattern.exec(withoutNew);
        if (match && match.index >= 0) {
            splitIndex = match.index;
            splitLength = match[0].length;
            break;
        }
    }

    const rawTitle =
        splitIndex >= 0 ? withoutNew.slice(0, splitIndex) : withoutNew;
    const title = rawTitle.trim();
    if (!title) return item;
    const remainder = splitIndex >= 0 ? withoutNew.slice(splitIndex) : "";

    return (
        <>
            {leadingWhitespace}
            <span className="app-changelog-new-badge">New</span>{" "}
            <span className="app-changelog-new-title">{title}</span>
            {remainder}
        </>
    );
}

export function ChangelogModal({
    open,
    onClose,
    dialogId,
}: ChangelogModalProps) {
    const titleId = useId();

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <>
            <div
                className="app-changelog-backdrop"
                aria-hidden
                onClick={onClose}
            />
            <div
                id={dialogId}
                className="app-changelog-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
            >
                <div className="panel-mobile-popover-header">
                    <span
                        id={titleId}
                        className="panel-mobile-popover-title"
                    >
                        Changelog
                    </span>
                    <button
                        type="button"
                        className="panel-mobile-popover-close"
                        onClick={onClose}
                        aria-label="Close changelog"
                    >
                        x
                    </button>
                </div>
                <div className="app-changelog-body panel-mobile-popover-body">
                    {CHANGELOG.map((entry) => (
                        <section
                            key={entry.version}
                            className="app-changelog-section"
                        >
                            <h2 className="app-changelog-version">
                                {entry.version}
                                {entry.date ? (
                                    <span className="app-changelog-date">
                                        {" "}
                                        - {entry.date}
                                    </span>
                                ) : null}
                            </h2>
                            <ul className="app-changelog-list">
                                {entry.items.map((item, i) => (
                                    <li key={i}>{renderChangelogItem(item)}</li>
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </div>
        </>
    );
}
